<template>
  <v-container
    v-if="charger"
    class="pa-5"
    :class="{ 'bg-pwt_alabaster': variant === 'modal', 'bg-pwt_snow': variant === 'dashboard' }"
  >
    <v-container
      class="pt-0"
      :class="{ 'bg-pwt_alabaster': variant === 'modal', 'bg-pwt_snow': variant === 'dashboard' }"
    >
      <!-- title band -->
      <div class="d-flex flex-column flex-md-row align-md-center align-start mb-5">
        <v-skeleton-loader
          type="heading, subtitle"
          width="50%"
          v-if="loading"
          class="ml-n4"
          :class="{
            'bg-pwt_alabaster': variant === 'modal',
            'bg-pwt_snow': variant === 'dashboard',
          }"
        />
        <div class="flex-grow-1 flex-shrink-1" v-else>
          <v-card-title class="py-0 pl-0">
            {{ charger.name }}
          </v-card-title>
          <v-card-subtitle class="pl-0" opacity="1">
            {{ charger.addressString }}
          </v-card-subtitle>
        </div>
        <div class="flex-grow-1 flex-shrink-1" style="width: 100%">
          <div class="d-flex justify-start justify-md-end">
            <div
              class="d-flex align-end flex-column"
              :class="{ 'pt-2': mobile }"
              style="width: 100%"
              :style="mobile ? 'max-width: 100%' : 'max-width: 220px'"
            >
              <OutlinedBlockBtn
                :shape="mobile ? 'box' : 'pill'"
                class="mt-1"
                @click="showSaveChargingStopDialog = true"
                :disabled="isSaved"
              >
                {{ isSaved ? 'Already saved' : 'Add to saved...' }}
              </OutlinedBlockBtn>
            </div>
          </div>
        </div>
      </div>
      <!-- basic details section -->
      <ul class="mb-5 text-body-1" style="list-style: none; padding-left: unset">
        <li>data provided by {{ charger.dataSrcDisplayStr }}</li>
        <li>{{ charger.evses.length }} devices</li>
        <li>
          {{ charger.simpleDisplayRating }}
        </li>
      </ul>
      <!-- available connectors with BYO cable notif -->
      <v-card-text
        class="pl-0 mb-10"
        v-if="
          charger.chargingCapabilitiesDisplayData.some((d) => d.friendlyName.includes('Socketed'))
        "
      >
        <v-card
          color="pwt_snow"
          style="
            border: 1px solid rgb(var(--v-theme-pwt_azure));
            border-radius: 8px;
            box-shadow: 0 5px 13px 0 rgba(0, 0, 0, 0.07);
            padding-top: 22px;
          "
          flat
        >
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-2"> mdi-map-marker-alert-outline </v-icon>
            Please bring your own cables
          </v-card-title>
          <v-card-text style="padding: 12px 54px 40px">
            This station has socketed connectors so you will have to bring your own cables to charge
            your vehicle. Please check for
            <span style="text-decoration: underline">‘socketed connectors’</span> below to see what
            cables you will need to bring.
          </v-card-text>
          <v-row no-gutters class="flex-wrap" style="padding: 0 54px 20px">
            <v-col
              cols="6"
              md="4"
              lg="3"
              v-for="(connector, index) in charger.chargingCapabilitiesDisplayData"
              :key="connector.friendlyName"
            >
              <v-card
                flat
                class="ma-1 pa-2 d-flex align-center no-wrap overflow-hidden"
                :class="{ 'ml-0': mobile ? index % 2 == 0 : index % 4 == 0 }"
                color="pwt_alabaster"
                style="border: 1px solid rgb(var(--v-theme-pwt_off_white)); border-radius: 10px"
              >
                <img :src="connector.iconURL" style="width: 32px; height: 32px" class="mr-2" />
                <span class="text-truncate">{{ connector.friendlyName }}</span>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-card-text>
      <!-- available connectors -->
      <v-card-text class="pl-0" v-else>
        Available connectors
        <v-row no-gutters class="flex-wrap">
          <v-col
            cols="6"
            md="4"
            lg="3"
            v-for="(connector, index) in charger.chargingCapabilitiesDisplayData"
            :key="connector.friendlyName"
          >
            <v-card
              class="ma-1 pa-2 d-flex align-center no-wrap overflow-hidden"
              :class="{ 'ml-0': mobile ? index % 2 == 0 : index % 4 == 0 }"
              color="pwt_snow"
            >
              <img :src="connector.iconURL" style="width: 32px; height: 32px" class="mr-2" />
              <span class="text-truncate">{{ connector.friendlyName }}</span>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
      <!-- booking band -->
      <ChargerBookingBand
        v-if="charger.isPrivate && fleet && fleet.allowPrivateChargerBooking"
        :charger="charger"
      />

      <!-- available devices section -->
      <v-card-title class="mt-5 pl-0"> Available devices </v-card-title>
      <v-row no-gutters class="flex-wrap">
        <v-col
          cols="12"
          md="6"
          v-for="(evse, index) in charger.evses"
          :key="evse.uid"
          class="py-2"
          :class="{ 'pr-2': !mobile && index % 2 == 0, 'pl-2': !mobile && index % 2 == 1 }"
        >
          <DeviceCard
            :device="evse"
            :bookable-device-data="
              charger.bookableEVSEDisplayData.find((evseData) => evseData.evse_id === evse.uid)
            "
          />
        </v-col>
      </v-row>

      <!-- Actual charging stop info card -->
      <ChargingStopDetailsCard
        v-if="trip && charger && trip.chargingStopCDBIDs.includes(charger.id)"
      />
      <!-- Estimated charging stop info card -->
      <ChargingCalculatorCard v-else-if="charger && vehicle && vehicle.evModel" />
      <!-- no EV model set error display -->
      <v-card class="pa-2 mt-5 mb-5 rounded-lg" v-else>
        <v-card-text> Please set an EV profile for your EV to see estimates. </v-card-text>
      </v-card>
      <!-- add charger section -->
      <v-card v-if="showAddChargingStopButton" class="mb-5 rounded-lg" color="pwt_snow">
        <v-card-title> Add charger to your trip </v-card-title>
        <v-card-text>
          <v-slider
            v-model="chargeTo"
            min="1"
            max="100"
            step="1"
            track-color="grey-lighten-2"
            :label="'Charge up to ' + chargeTo + '%'"
            thumb-size="16"
            track-size="6"
            color="primary"
          />
          <v-card-title class="text--subtitle-2"> Add to leg </v-card-title>
          <v-list bg-color="background" color="primary">
            <v-list-item
              v-for="(item, i) in stepOrder"
              :key="i"
              :model-value="item"
              :active="item.stepIndex === selectedStep?.stepIndex"
              @click="() => (selectedStep = item)"
            >
              <v-list-item-title>
                {{ item.displayName }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
          <ElevatedBlockBtn class="mt-5" @click="addChargingStop">
            Add charging stop
          </ElevatedBlockBtn>
        </v-card-text>
      </v-card>
      <!-- Support section -->
      <v-card
        v-if="
          (branding && branding.instructionsUrl) ||
          (branding && branding.faqsUrl) ||
          (branding && branding.emergencyContactNumber)
        "
        class="mb-3 rounded-lg pa-1"
        color="pwt_snow"
      >
        <v-row no-gutters align="center" class="pr-3">
          <div>
            <v-card-title class="pb-0"> Customer support </v-card-title>
            <v-card-subtitle opacity="1">
              Operated by {{ branding?.displayName ?? charger.operator?.name  ?? 'unknown' }}
            </v-card-subtitle>
          </div>
          <v-spacer v-if="charger.operator && charger.operator.name"></v-spacer>
          <OperatorLogo :networkBranding="branding" />
        </v-row>
        <v-card-text>
          <ul style="list-style: none; padding-left: unset">
            <template
              v-if="
                branding.instructionsUrl &&
                branding.faqsUrl &&
                branding.faqsUrl === branding.instructionsUrl
              "
            >
              <li>Full instructions and FAQs at</li>
              <li class="mb-3">{{ branding.instructionsUrl }}</li>
            </template>
            <template v-else>
              <template v-if="branding?.instructionsUrl">
                <li>Full instructions at</li>
                <li class="mb-3">{{ branding.instructionsUrl }}</li>
              </template>
              <template v-if="branding?.faqsUrl">
                <li>FAQs</li>
                <li class="mb-3">{{ branding.faqsUrl }}</li>
              </template>
            </template>
            <template v-if="branding?.emergencyContactNumber">
              <li>Emergency support line</li>
              <li class="mb-3">{{ branding.emergencyContactNumber }}</li>
            </template>
          </ul>
        </v-card-text>
      </v-card>
    </v-container>
    <GenericDialog
      :activator="showSaveChargingStopDialog"
      @close="showSaveChargingStopDialog = false"
    >
      <v-card flat style="height: 70vh">
        <template v-if="isSaved">
          <v-card-title class="text-center mt-15"> '{{ charger.name }}' has been saved </v-card-title>
          <v-card-text class="text-center" style="height: 50%">
            Charging station has been saved to your saved charging stations. You can edit or delete this by navigating to your saved chargers on your dashboard.
          </v-card-text>
          <v-card-actions class="justify-center">
            <OutlinedBtn @click="showSaveChargingStopDialog = false" shape="box" class="mr-2">
              Exit
            </OutlinedBtn>
            <ElevatedBtn @click="navigateToSavedChargers" shape="box"> Go to Saved </ElevatedBtn>
          </v-card-actions>
        </template>
        <template v-else>
          <v-card-title class="text-center mt-15"> Save '{{ charger.name }}'? </v-card-title>
          <v-card-text class="text-center" style="height: 50%">
            You can save '{{ charger.name }}' to your saved chargers
          </v-card-text>
          <v-card-actions class="justify-center">
            <OutlinedBtn @click="showSaveChargingStopDialog = false" shape="box" class="mr-2">
              Cancel
            </OutlinedBtn>
            <ElevatedBtn @click="saveChargingStop" shape="box" class="ml-2"> Continue </ElevatedBtn>
          </v-card-actions>
        </template>
      </v-card>
    </GenericDialog>
  </v-container>
</template>

<script setup lang="ts">
import Charger from '@/classes/charger_classes/charger'
import type Connector from '@/classes/charger_classes/connector'
import type NetworkBranding from '@/classes/charger_classes/networkBranding'
import Trip from '@/classes/trip_classes/trip'
import type Vehicle from '@/classes/vehicle_classes/vehicle'
import ElevatedBlockBtn from '@/components/ui-elements/buttons/ElevatedBlockBtn.vue'
import { GettersTypes, MutationTypes, type State } from '@/store/store_types'
import { fillChargerMissingData } from '@/utils/fillChargerMissingData'
import { findChargersNetworkBranding } from '@/utils/findChargersNetworkBranding'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import GenericDialog from '../dialog-wrappers/GenericDialog.vue'
import ElevatedBtn from '../ui-elements/buttons/ElevatedBtn.vue'
import OutlinedBlockBtn from '../ui-elements/buttons/OutlinedBlockBtn.vue'
import OutlinedBtn from '../ui-elements/buttons/OutlinedBtn.vue'
import ChargerBookingBand from './ChargerBookingBand.vue'
import ChargingCalculatorCard from './ChargingCalculatorCard.vue'
import ChargingStopDetailsCard from './ChargingStopDetailsCard.vue'
import DeviceCard from './DeviceCard.vue'
import OperatorLogo from './OperatorLogo.vue'

const { variant = 'modal' } = defineProps<{ variant?: 'modal' | 'dashboard' }>()

const store = useStore<State>()
const { mobile } = useDisplay()

const selectedConnector = computed(() => {
  if (selectedConnectorId.value) {
    return (
      store.getters[GettersTypes.selectedChargerData] as Charger | undefined
    )?.allConnectors.find((connector) => connector.id === selectedConnectorId.value)
  }
  return undefined
})

const charger = computed<Charger | undefined>(() => store.getters[GettersTypes.selectedChargerData])
const branding = computed<NetworkBranding | undefined>(
  () => store.getters[GettersTypes.selectedChargerBranding],
)
const fleet = computed(() => store.state.fleet)
const trip = computed<Trip | undefined>(() => store.getters[GettersTypes.selectedTripData])
const vehicle = computed<Vehicle | undefined>(() => {
  if (trip.value) {
    return trip.value.vehicle
  } else return store.getters[GettersTypes.selectedVehicleData]
})

const showAddChargingStopButton = computed(() => {
  return (
    charger.value &&
    trip.value &&
    !trip.value.itinerary.chargerIDs.includes(charger.value.id) &&
    vehicle.value &&
    charger.value.isCompatible(vehicle.value) !== 'incompatible'
  )
})

const chargingCostPerMin = computed(() => store.state.defaultCostPerMinDC)
const publicCostPerKWh = computed(() => store.state.defaultPublicCostPerKWh)

const stepOrder = computed(() => {
  if (!trip.value) return []
  if (!charger.value) return []
  return trip.value.itinerary.orderStepsByDistanceFromPoint({
    lat: charger.value.coordinates.latitude,
    lon: charger.value.coordinates.longitude,
  })
})

const selectedConnectorId = ref<string | undefined>(undefined)
const chargeTo = ref(80)
const selectedStep = ref<{
  distanceScore: number
  stepIndex: number
  pointInBounds: boolean
  displayName: string
} | null>(null)
const fetching = ref(false)

watch(trip, () => {
  if (trip.value) {
    fetchCorridorCall()
  }
})

watch(
  stepOrder,
  () => {
    selectedStep.value = stepOrder.value[0] ?? null
  },
  { immediate: true },
)

const loading = ref(true)

onMounted(async () => {
  await nextTick()
  setInitialValues()
  await checkForMissingData()
  loading.value = false
})

async function checkForMissingData(): Promise<void> {
  if ((charger.value && !charger.value.address) || (charger.value && !charger.value.name)) {
    const network = findChargersNetworkBranding(charger.value, store.state.networkBranding)
    const filledStation = await fillChargerMissingData(charger.value, network)
    store.commit(MutationTypes.updateIndividualCharger, filledStation)
  }
}

function close() {
  store.commit(MutationTypes.setSelectedCharger, undefined)
  store.commit(MutationTypes.setMainDialogContent, undefined)
}

function selectConnector(uuid: string): void {
  selectedConnectorId.value = uuid
}

function setInitialValues(): void {
  if (vehicle.value && charger.value) {
    const connectors = charger.value.allConnectors.filter(
      (connector) => connector.isCompatible(vehicle.value!) !== 'incompatible',
    )
    if (connectors.length) selectConnector((connectors[0] as Connector).id)
  } else if (charger.value && charger.value.evses[0] && charger.value.evses[0].connectors[0]) {
    selectConnector(charger.value.evses[0].connectors[0].id)
  }
}

async function addChargingStop(): Promise<void> {
  if (!charger.value) return
  if (!trip.value) return
  if (!selectedStep.value) return
  await trip.value.addChargerToStep({
    stepIndex: selectedStep.value.stepIndex,
    charger: charger.value,
    stateOfChargeAfterCharging: chargeTo.value / 100,
    defaultCostPerKWh: publicCostPerKWh.value,
    defaultCostPerMinDC: chargingCostPerMin.value,
  })
  store.commit(MutationTypes.updateIndividualTrip, trip.value)
  close()
}

async function fetchCorridorCall(): Promise<void> {
  fetching.value = true
  if (!trip.value) return
  const outcome = await trip.value.getRadarData()
  if (outcome === 'SUCCESS') {
    store.commit(MutationTypes.updateIndividualTrip, trip.value)
  }
  fetching.value = false
}

const showSaveChargingStopDialog = ref(false)
const isSaved = computed(
  () => !!(charger.value && store.state.savedChargers.some((s) => s.CDBID === charger.value!.id)),
)

async function saveChargingStop(): Promise<void> {
  store.commit(MutationTypes.addSavedCharger, {
    CDBID: charger.value!.id,
  })
}

function navigateToSavedChargers() {
  close()
  store.commit(MutationTypes.setDashboardDialogContent, 'saved-charger-list')
  store.commit(MutationTypes.setShowDashboard, true)
}
</script>

<style scoped>
#pwt-custom-active-tab {
  border-radius: 10px 10px 0 0;
  background-color: var(--v-theme-primary);
}

.pwt-tabs-border {
  border-bottom: 2px solid var(--v-theme-primary);
}

.pwt-custom-active-tab-text {
  z-index: 1500;
}

* :deep(.v-slider__track-fill) {
  border-radius: 2px; /* override default slider border-radius */
}

* :deep(.v-slider__track-background) {
  border-radius: 2px; /* override default slider border-radius */
}
</style>
